import { navigateTo, useRoute } from '#imports'
import { type Ref, onMounted, ref, watch } from 'vue'
import type { LocationQuery } from 'vue-router'

import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

const DEFAULT_PAGE = 1

/**
 * Bumped whenever the shape of the persisted payload changes, so a blob written
 * by a previous version is ignored rather than half-restored.
 */
const STORAGE_VERSION = 1

type BaseFiltersValues = {
  page?: number
  [key: string]: unknown
}

type Query = LocationQuery | Record<string, number | string>

type StorageOptions<T> = {
  /** localStorage key holding the user's last search. */
  key: string
  /**
   * Subset of the filters keys to remember across visits. Anything left out
   * (typically `page`, `pageSize` or sorting) lives in the URL only.
   */
  persist: Array<keyof T & string>
}

type UseFiltersOptions<T> = {
  isUsingCursorPagination?: boolean
  /**
   * Opt-in "last search" memory. Omit it to keep the URL-only behaviour.
   */
  storage?: StorageOptions<T>
  /**
   * Keys holding a list of values. Vue Router serialises a single-element array
   * as a scalar (`?grades=A`), so those keys must be coerced back to an array on
   * the way in, otherwise the form and the API receive a bare string.
   */
  arrayKeys?: Array<keyof T & string>
}

type PersistedPayload = {
  version: number
  values: Record<string, unknown>
}

function toArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value
  if (value === null || value === undefined || value === '') return []

  return [value]
}

/**
 * This composable is a all-in-one filter system for pages
 * which aims to sync filters' values with the URL query parameters.
 *
 * The URL is the single source of truth. When `options.storage` is provided,
 * the persisted values are used as a fallback for a "cold" landing (a URL
 * carrying no known filter), and the URL is then normalised to match.
 */
export function useFilters<T extends BaseFiltersValues>(
  initialFiltersValues: T,
  options: UseFiltersOptions<T> = {},
) {
  const { isUsingCursorPagination = false, storage, arrayKeys } = options

  const route = useRoute()
  const localStorage = useLocalStorage()

  /**
   * We filter the query to keep only the allowed keys.
   * This is to prevent unwanted query-params to be used by form or sent to API.
   */
  function getSafeQuery(allowedKeys: string[], query: Query): T {
    return allowedKeys.reduce(
      (acc, key) => (key in query ? { ...acc, [key]: query[key] } : acc),
      {} as T,
    )
  }

  function coerceArrays(values: Partial<T>): Partial<T> {
    if (!arrayKeys?.length) return values

    return arrayKeys.reduce(
      (acc, key) => (key in acc ? { ...acc, [key]: toArray(acc[key]) } : acc),
      { ...values },
    )
  }

  const filtersKeys = Object.keys(initialFiltersValues)
  const safeQuery = coerceArrays(getSafeQuery(filtersKeys, route.query)) as T

  /**
   * Read the last search back from localStorage.
   *
   * This is deliberately all-or-nothing: as soon as the URL carries at least
   * one known filter we honour it verbatim. Merging the two would let a shared
   * link silently inherit the recipient's own saved values for the params the
   * link omits.
   */
  function readPersistedValues(): Partial<T> {
    if (!storage || !isEmpty(safeQuery)) return {}

    const rawPayload = localStorage.getItem(storage.key)

    if (!rawPayload) return {}

    try {
      const payload = JSON.parse(rawPayload) as PersistedPayload

      if (payload?.version !== STORAGE_VERSION) return {}

      return coerceArrays(
        getSafeQuery(storage.persist, payload.values as Query),
      )
    } catch {
      // Corrupted payload: fall back to the defaults rather than breaking setup.
      return {}
    }
  }

  function persistValues(values: Partial<T>): void {
    if (!storage) return

    const payload: PersistedPayload = {
      version: STORAGE_VERSION,
      values: getSafeQuery(storage.persist, values as Query),
    }

    localStorage.setItem(storage.key, JSON.stringify(payload))
  }

  const persistedValues = readPersistedValues()

  /**
   * Merges a partial set of values over the defaults, making sure `page` always
   * ends up a number to ease its consumption with pagination.
   */
  function buildFiltersValues(...sources: Array<Partial<T>>): T {
    const merged = sources.reduce<Partial<T>>(
      (acc, source) => ({ ...acc, ...source }),
      { ...initialFiltersValues },
    )

    if (isUsingCursorPagination) return merged as T

    const page = sources.reduce<unknown>(
      (acc, source) => source.page ?? acc,
      initialFiltersValues.page,
    )

    return {
      ...merged,
      page: parseInt(page?.toString() || DEFAULT_PAGE.toString(), 10),
    } as T
  }

  // TODO: We are using this nasty cast because active filters is typed as UnwrapedRef
  const filtersValues = ref<T>(
    buildFiltersValues(persistedValues, safeQuery),
  ) as Ref<T>

  const cleanedValues = removeEmptyValuesInObject<T>(filtersValues.value)

  const sanitizedFiltersValues = ref(cleanedValues) as Ref<Partial<T>>

  /**
   * Canonical form of a query, used to tell our own navigations apart from the
   * browser's.
   *
   * Everything is normalised to arrays of strings because a round trip through
   * the URL is lossy in two ways: a number comes back as a string, and a
   * single-element array comes back as a scalar. Comparing raw values would
   * report a difference on every navigation and cause a spurious refetch.
   */
  function canonicalQuery(query: Query): string {
    const entries = query as Record<string, unknown>

    return JSON.stringify(
      Object.keys(entries)
        .sort()
        .map((key) => [key, toArray(entries[key]).map(String)]),
    )
  }

  let lastWrittenQuery = canonicalQuery(cleanedValues as Query)

  /**
   * Re-hydrate when the URL changes without going through `setFilters`, which is
   * what the browser's back and forward buttons do.
   */
  watch(
    () => route.query,
    (query) => {
      const incoming = canonicalQuery(query)

      if (incoming === lastWrittenQuery) return

      lastWrittenQuery = incoming

      filtersValues.value = buildFiltersValues(
        coerceArrays(getSafeQuery(filtersKeys, query)) as T,
      )
      sanitizedFiltersValues.value = removeEmptyValuesInObject<T>(
        filtersValues.value,
      )
    },
  )

  onMounted(async () => {
    // Makes the URL canonical, including the values just restored from storage.
    // `replace` keeps this normalisation out of the browser history.
    await navigateTo({
      ...route,
      hash: route.hash,
      query: cleanedValues as Query,
      replace: true,
    })
  })

  async function setFilters(values: T, { persist = true } = {}) {
    let processedValues = values

    if (isUsingCursorPagination) {
      processedValues = {
        ...values,
        page: undefined,
      }
    }

    // We keep the empty string values to satisfy form components that needs string
    filtersValues.value = getSafeQuery(filtersKeys, processedValues as Query)

    // We remove the empty string values to send to the API and query params
    const query = removeEmptyValuesInObject(processedValues) as Query

    sanitizedFiltersValues.value = getSafeQuery(filtersKeys, query)

    if (persist) {
      persistValues(sanitizedFiltersValues.value)
    } else if (storage) {
      localStorage.removeItem(storage.key)
    }

    // Claim this navigation so the route watcher does not treat it as a
    // back/forward and re-hydrate on top of what we just committed.
    lastWrittenQuery = canonicalQuery(query)

    await navigateTo({
      ...route,
      hash: route.hash,
      query,
    })
  }

  async function resetFilters() {
    // A reset must also forget the last search, otherwise the next cold landing
    // would restore the filters the user just cleared.
    await setFilters(
      { ...initialFiltersValues, page: DEFAULT_PAGE },
      { persist: false },
    )
  }

  async function submitFilters() {
    await setFilters({ ...filtersValues.value, page: DEFAULT_PAGE })
  }

  async function setCurrentPage(page: number) {
    filtersValues.value.page = page
    await setFilters(filtersValues.value)
  }

  return {
    /**
     * Restore the values to the given initialValues passed as argument,
     * and forget the persisted last search when `options.storage` is used
     */
    resetFilters,
    /**
     * Commit the in-memory filters' values used as v-model in the URL
     */
    submitFilters,
    /**
     * Set the the current page value and submit the filters
     */
    setCurrentPage,
    /**
     * The v-model to bind to your form inputs
     */
    filtersValues,
    /*
     * Those values are the ones stored in the URL query-params
     * to be used to send to the API.
     */
    sanitizedFiltersValues,
  }
}
